import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, Plus, Users } from 'lucide-react';

import { listDepartments, listEmployees } from '@/api/employees.api';
import {
  closeRound,
  createRound,
  getMyResults,
  listRounds,
  listRoundScores,
  parseCoreValues,
  upsertScore,
  type CoreValueRating,
  type MyRoundResult,
  type PaRound,
  type PaScore,
} from '@/api/eos/peopleAnalyzer.api';
import { getVto, normalizeVtoData } from '@/api/eos/vto.api';
import { summarizeShoutOuts, type MemberShoutOutSummary } from '@/api/comms/shoutOuts.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { describeEosError } from '@/lib/eos/errors';
import { quarterLabel, rollingQuarters, toIsoDate } from '@/lib/eos/quarters';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * EOS People Analyzer — Stream B Wave 3 (ADR-023).
 *
 * Core-values + GWC reviews against "the Bar". Two surfaces on one page:
 *   - "My reviews" (everyone): CLOSED-round aggregates about YOU, raters
 *     anonymized server-side.
 *   - The leadership grid (dept_lead+): rate subjects per round. Raw
 *     scores are leadership-only; the server scopes department leads to
 *     their own department.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const RATING_LABEL: Record<CoreValueRating, string> = { plus: '+', plus_minus: '±', minus: '−' };
const RATING_ORDER: CoreValueRating[] = ['plus', 'plus_minus', 'minus'];

type GwcAnswer = boolean | null;
type GwcKey = 'getsIt' | 'wantsIt' | 'capacity';
const GWC_META: { key: GwcKey; short: string; label: string }[] = [
  { key: 'getsIt', short: 'G', label: 'Gets it' },
  { key: 'wantsIt', short: 'W', label: 'Wants it' },
  { key: 'capacity', short: 'C', label: 'Capacity to do it' },
];

export function PeopleAnalyzerPage() {
  const { state: authState } = useAuth();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;
  const canManage = me !== null && (isSuperadmin(me) || hasCapability(me, 'manage_eos'));
  const isDeptLead = me?.membership.role === 'department_lead';
  const isLeader = canManage || isDeptLead;

  const myResultsQuery = useQuery({
    queryKey: ['eos', 'people-analyzer', 'me'],
    queryFn: getMyResults,
  });

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <h1 className="typo-h1">People Analyzer</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Core values + GWC against the Bar. Your own results appear once a round closes.
        </p>
      </div>

      {/* ── My reviews (everyone) ────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-kw-muted">My reviews</h2>
        {myResultsQuery.isLoading ? (
          <p className="text-[13px] text-kw-muted">Loading your results…</p>
        ) : myResultsQuery.error ? (
          <Card className="p-4">
            <p className="text-[13px] text-kw-danger-text">
              {describeEosError(myResultsQuery.error, 'Could not load your results.')}
            </p>
          </Card>
        ) : (myResultsQuery.data ?? []).length === 0 ? (
          <Card className="flex items-center gap-3 p-5">
            <Users className="size-6 shrink-0 text-kw-ghost" />
            <p className="text-[13px] text-kw-muted">
              No closed review rounds yet — your aggregated, anonymous results will show here after a round
              closes.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {(myResultsQuery.data ?? []).map((round) => (
              <MyRoundCard key={round.roundId} result={round} />
            ))}
          </div>
        )}
      </section>

      {/* ── Leadership grid (dept_lead+) ─────────────────────────────── */}
      {isLeader ? (
        <LeadershipGrid canManage={canManage} isDeptLead={isDeptLead} myMembershipId={myMembershipId} />
      ) : (
        <Card className="flex items-center gap-3 p-5">
          <Lock className="size-5 shrink-0 text-kw-ghost" />
          <p className="text-[13px] text-kw-muted">
            The review grid is a leadership tool — department leads and admins rate their teams here. Your own
            results appear above under “My reviews”.
          </p>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// My reviews — closed-round aggregates
// ─────────────────────────────────────────────────────────────────────────

function MyRoundCard({ result }: { result: MyRoundResult }) {
  // Aggregate each anonymous rater snapshot into per-value rating counts.
  const valueCounts = useMemo(() => {
    const counts = new Map<string, Record<CoreValueRating, number>>();
    for (const snapshot of result.coreValues) {
      for (const { value, rating } of parseCoreValues(snapshot)) {
        const entry = counts.get(value) ?? { plus: 0, plus_minus: 0, minus: 0 };
        entry[rating] += 1;
        counts.set(value, entry);
      }
    }
    return [...counts.entries()];
  }, [result.coreValues]);

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold text-kw-shark">{result.roundLabel}</h3>
        {result.closedAt ? (
          <span className="text-[12px] text-kw-muted">
            Closed {new Date(result.closedAt).toLocaleDateString()}
          </span>
        ) : null}
      </div>

      {valueCounts.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {valueCounts.map(([value, counts]) => (
            <li key={value} className="flex items-center justify-between gap-2 text-[13px]">
              <span className="min-w-0 flex-1 truncate text-kw-shark">{value}</span>
              <span className="flex shrink-0 gap-2 text-[12px]">
                <span className="text-kw-tag-text">+ {counts.plus}</span>
                <span className="text-kw-warning-text">± {counts.plus_minus}</span>
                <span className="text-kw-danger-text">− {counts.minus}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-[13px] italic text-kw-muted">No core-value ratings in this round.</p>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2">
        {GWC_META.map(({ key, label }) => (
          <div key={key} className="rounded-kw-md bg-kw-bg px-3 py-2 text-center">
            <p className="text-[11px] font-medium uppercase tracking-wide text-kw-muted">{label}</p>
            <p className="mt-0.5 text-[13px] text-kw-shark">
              <span className="text-kw-tag-text">{result.gwc[key].yes} yes</span>
              {' · '}
              <span className="text-kw-danger-text">{result.gwc[key].no} no</span>
            </p>
          </div>
        ))}
      </div>

      {result.notes.length > 0 ? (
        <div className="mt-3 space-y-1.5 border-t border-kw-border pt-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-kw-muted">Anonymous notes</p>
          {result.notes.map((note, i) => (
            // Notes are anonymous free text — no stable identity beyond position.
            <p key={i} className="text-[13px] italic text-kw-muted">
              “{note}”
            </p>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Leadership grid
// ─────────────────────────────────────────────────────────────────────────

function LeadershipGrid({
  canManage,
  isDeptLead,
  myMembershipId,
}: {
  canManage: boolean;
  isDeptLead: boolean;
  myMembershipId: string | null;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const roundsQuery = useQuery({
    queryKey: ['eos', 'people-analyzer', 'rounds'],
    queryFn: listRounds,
  });
  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
  });
  const vtoQuery = useQuery({ queryKey: ['eos', 'vto'], queryFn: () => getVto() });

  const rounds = useMemo(() => roundsQuery.data ?? [], [roundsQuery.data]);
  const selectedRound = rounds.find((r) => r.id === selectedRoundId) ?? rounds[0] ?? null;

  const scoresQuery = useQuery({
    queryKey: ['eos', 'people-analyzer', 'scores', selectedRound?.id],
    queryFn: () => listRoundScores(selectedRound?.id ?? ''),
    enabled: selectedRound !== null,
  });

  // Core values from the CURRENT company V/TO — snapshotted into each
  // score on save so later V/TO edits never corrupt review history.
  const coreValues = useMemo(() => {
    const current = vtoQuery.data?.current;
    if (!current) return [];
    return normalizeVtoData(current.dataJson).coreValues.filter((v) => v.trim() !== '');
  }, [vtoQuery.data]);

  // Live department of the signed-in lead, resolved via the directory.
  const myDepartmentId = useMemo(() => {
    if (myMembershipId === null) return null;
    const mine = (employeesQuery.data?.data ?? []).find((e) => e.membershipId === myMembershipId);
    return mine?.departmentId ?? null;
  }, [employeesQuery.data, myMembershipId]);

  const subjects = useMemo(() => {
    let list = (employeesQuery.data?.data ?? []).filter((e) => e.membershipId !== null);
    if (selectedRound?.departmentId) {
      list = list.filter((e) => e.departmentId === selectedRound.departmentId);
    }
    // Department leads without manage_eos may only score their own department.
    if (!canManage && isDeptLead) {
      list = list.filter((e) => e.departmentId !== null && e.departmentId === myDepartmentId);
    }
    return list;
  }, [employeesQuery.data, selectedRound, canManage, isDeptLead, myDepartmentId]);

  // Recognition (90d) evidence strip — shout-out counts per subject.
  // Display-only; never written into PA rows (ADR-027 §7.7).
  const subjectMembershipIds = useMemo(
    () => subjects.map((s) => s.membershipId).filter((id): id is string => id !== null),
    [subjects],
  );
  const recognitionQuery = useQuery({
    queryKey: ['comms', 'shout-outs', 'summary', subjectMembershipIds],
    queryFn: () => summarizeShoutOuts({ membershipIds: subjectMembershipIds, sinceDays: 90 }),
    enabled: subjectMembershipIds.length > 0,
  });
  const recognitionByMember = useMemo(() => {
    const map = new Map<string, MemberShoutOutSummary>();
    for (const m of recognitionQuery.data ?? []) map.set(m.membershipId, m);
    return map;
  }, [recognitionQuery.data]);

  const closeMutation = useMutation({
    mutationFn: (roundId: string) => closeRound(roundId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['eos', 'people-analyzer'] });
      toast.show('Round closed — results are now visible to the people reviewed.', {
        variant: 'success',
      });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not close the round.'), { variant: 'danger' });
    },
  });

  function invalidateScores(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'people-analyzer', 'scores'] });
  }

  const isClosed = selectedRound?.status === 'closed';

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-kw-muted">Review rounds</h2>
        {canManage ? (
          <Button
            size="sm"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4" />
            New round
          </Button>
        ) : null}
      </div>

      {roundsQuery.isLoading ? (
        <p className="text-[13px] text-kw-muted">Loading rounds…</p>
      ) : roundsQuery.error ? (
        <Card className="p-4">
          <p className="text-[13px] text-kw-danger-text">
            {describeEosError(roundsQuery.error, 'Could not load review rounds.')}
          </p>
        </Card>
      ) : rounds.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <Users className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No review rounds yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {canManage
                ? 'Start a round to review the team against your core values and GWC.'
                : 'An admin starts rounds — they will appear here.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px,1fr]">
          {/* Rounds sidebar */}
          <div className="space-y-2">
            {rounds.map((round) => (
              <button
                key={round.id}
                type="button"
                onClick={() => {
                  setSelectedRoundId(round.id);
                }}
                className={`w-full rounded-kw-lg border p-3 text-left transition-colors ${
                  selectedRound?.id === round.id
                    ? 'border-kw-cerulean bg-kw-cerulean-light'
                    : 'border-kw-border bg-white hover:bg-kw-bg'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-kw-shark">
                    {round.label}
                  </span>
                  <Badge variant={round.status === 'closed' ? 'neutral' : 'success'}>
                    {round.status === 'closed' ? 'Closed' : 'Open'}
                  </Badge>
                </div>
                {round.quarterStart ? (
                  <p className="mt-0.5 text-[11px] text-kw-muted">
                    {quarterLabel(new Date(round.quarterStart))}
                  </p>
                ) : null}
              </button>
            ))}
          </div>

          {/* Score grid */}
          {selectedRound ? (
            <Card className="p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-[14px] font-semibold text-kw-shark">{selectedRound.label}</h3>
                  {isClosed ? <Badge variant="neutral">Closed</Badge> : null}
                </div>
                {canManage && !isClosed ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={closeMutation.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Close this round? Scores lock and results become visible to the people reviewed.',
                        )
                      ) {
                        closeMutation.mutate(selectedRound.id);
                      }
                    }}
                  >
                    Close round
                  </Button>
                ) : null}
              </div>

              {coreValues.length === 0 ? (
                <p className="mb-3 rounded-kw-md bg-kw-warning-bg px-3 py-2 text-[12px] text-kw-warning-text">
                  No core values on the V/TO yet — define them there to rate against the Bar.
                </p>
              ) : null}

              {scoresQuery.isLoading || employeesQuery.isLoading ? (
                <p className="text-[13px] text-kw-muted">Loading scores…</p>
              ) : subjects.length === 0 ? (
                <p className="text-[13px] italic text-kw-muted">No members to review in this round.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left">
                    <thead>
                      <tr className="border-b border-kw-border">
                        <th className="py-2 pr-3 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
                          Member
                        </th>
                        {coreValues.map((value) => (
                          <th
                            key={value}
                            className="max-w-24 truncate px-2 py-2 text-center text-[11px] font-medium text-kw-muted"
                            title={value}
                          >
                            {value}
                          </th>
                        ))}
                        {GWC_META.map(({ short, label }) => (
                          <th
                            key={short}
                            title={label}
                            className="px-2 py-2 text-center text-[12px] font-medium text-kw-muted"
                          >
                            {short}
                          </th>
                        ))}
                        <th className="px-2 py-2 text-center text-[12px] font-medium uppercase tracking-wide text-kw-muted">
                          The Bar
                        </th>
                        <th className="py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.map((subject) => (
                        <SubjectRow
                          key={subject.membershipId ?? subject.id}
                          subjectName={`${subject.firstName} ${subject.lastName}`}
                          subjectMembershipId={subject.membershipId ?? ''}
                          roundId={selectedRound.id}
                          coreValues={coreValues}
                          myScore={(scoresQuery.data ?? []).find(
                            (s) =>
                              s.subjectMembershipId === subject.membershipId &&
                              s.ratedByMembershipId === myMembershipId,
                          )}
                          raterCount={
                            (scoresQuery.data ?? []).filter(
                              (s) => s.subjectMembershipId === subject.membershipId,
                            ).length
                          }
                          readOnly={isClosed}
                          recognition={
                            subject.membershipId ? recognitionByMember.get(subject.membershipId) : undefined
                          }
                          onSaved={invalidateScores}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ) : null}
        </div>
      )}

      <CreateRoundModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={(round) => {
          void queryClient.invalidateQueries({ queryKey: ['eos', 'people-analyzer', 'rounds'] });
          setSelectedRoundId(round.id);
          toast.show('Round created', { variant: 'success' });
        }}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// One subject's row — the caller's own review (rater = self)
// ─────────────────────────────────────────────────────────────────────────

function SubjectRow({
  subjectName,
  subjectMembershipId,
  roundId,
  coreValues,
  myScore,
  raterCount,
  readOnly,
  recognition,
  onSaved,
}: {
  subjectName: string;
  subjectMembershipId: string;
  roundId: string;
  coreValues: string[];
  myScore: PaScore | undefined;
  raterCount: number;
  readOnly: boolean;
  recognition: MemberShoutOutSummary | undefined;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [ratings, setRatings] = useState<Record<string, CoreValueRating | null>>({});
  const [gwc, setGwc] = useState<Record<GwcKey, GwcAnswer>>({
    getsIt: null,
    wantsIt: null,
    capacity: null,
  });
  const [notes, setNotes] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Seed local state from my saved score whenever it (re)loads.
  useEffect(() => {
    const saved: Record<string, CoreValueRating | null> = {};
    for (const { value, rating } of parseCoreValues(myScore?.coreValuesJson)) {
      saved[value] = rating;
    }
    setRatings(saved);
    setGwc({
      getsIt: myScore?.getsIt ?? null,
      wantsIt: myScore?.wantsIt ?? null,
      capacity: myScore?.capacity ?? null,
    });
    setNotes(myScore?.notes ?? '');
    setDirty(false);
  }, [myScore]);

  const saveMutation = useMutation({
    mutationFn: () =>
      upsertScore(roundId, {
        subjectMembershipId,
        coreValues: coreValues.flatMap((value) => {
          const rating = ratings[value];
          return rating ? [{ value, rating }] : [];
        }),
        getsIt: gwc.getsIt,
        wantsIt: gwc.wantsIt,
        capacity: gwc.capacity,
        notes,
      }),
    onSuccess: () => {
      setDirty(false);
      onSaved();
      toast.show(`Review of ${subjectName} saved`, { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not save the review.'), { variant: 'danger' });
    },
  });

  // "The Bar": every core value ≥ ± AND GWC all yes.
  const bar = useMemo<'pass' | 'fail' | 'incomplete'>(() => {
    const ratingValues = coreValues.map((v) => ratings[v] ?? null);
    const gwcValues = GWC_META.map(({ key }) => gwc[key]);
    if (ratingValues.some((r) => r === null) || gwcValues.some((g) => g === null)) return 'incomplete';
    if (ratingValues.every((r) => r !== 'minus') && gwcValues.every((g) => g === true)) return 'pass';
    return 'fail';
  }, [coreValues, ratings, gwc]);

  function cycleGwc(key: GwcKey): void {
    if (readOnly) return;
    setGwc((prev) => {
      const next: GwcAnswer = prev[key] === null ? true : prev[key] ? false : null;
      return { ...prev, [key]: next };
    });
    setDirty(true);
  }

  return (
    <>
      <tr className="border-b border-kw-border/60">
        <td className="py-2 pr-3">
          <p className="text-[13px] font-medium text-kw-shark">{subjectName}</p>
          <p className="text-[11px] text-kw-muted">
            {raterCount === 1 ? '1 review' : `${String(raterCount)} reviews`}
          </p>
          {recognition && recognition.total > 0 ? (
            <div
              className="mt-1 flex flex-wrap items-center gap-1"
              title={recognition.recent.map((r) => `“${r.message}” — ${r.giverNameSnapshot}`).join('\n')}
            >
              <span className="text-[10px] font-medium uppercase tracking-wide text-kw-muted">
                Recognition (90d)
              </span>
              {recognition.byValue.map((v) => (
                <span
                  key={v.coreValue}
                  className="rounded-kw-full bg-kw-cerulean-light px-1.5 py-0.5 text-[10px] text-kw-cerulean-dark"
                >
                  {v.coreValue} ×{v.count}
                </span>
              ))}
            </div>
          ) : null}
        </td>
        {coreValues.map((value) => (
          <td key={value} className="px-2 py-2 text-center">
            <div className="inline-flex overflow-hidden rounded-kw-md border border-kw-border">
              {RATING_ORDER.map((rating) => {
                const active = ratings[value] === rating;
                return (
                  <button
                    key={rating}
                    type="button"
                    disabled={readOnly}
                    aria-label={`Rate ${subjectName} on ${value}: ${RATING_LABEL[rating]}`}
                    aria-pressed={active}
                    onClick={() => {
                      setRatings((prev) => ({ ...prev, [value]: active ? null : rating }));
                      setDirty(true);
                    }}
                    className={`px-1.5 py-0.5 text-[12px] transition-colors disabled:cursor-not-allowed ${
                      active
                        ? rating === 'plus'
                          ? 'bg-kw-emerald-light text-kw-tag-text'
                          : rating === 'plus_minus'
                            ? 'bg-kw-warning-bg text-kw-warning-text'
                            : 'bg-kw-danger-bg text-kw-danger-text'
                        : 'bg-white text-kw-muted hover:bg-kw-bg'
                    }`}
                  >
                    {RATING_LABEL[rating]}
                  </button>
                );
              })}
            </div>
          </td>
        ))}
        {GWC_META.map(({ key, short, label }) => (
          <td key={key} className="px-2 py-2 text-center">
            <button
              type="button"
              disabled={readOnly}
              aria-label={`${label} for ${subjectName}: ${gwc[key] === null ? 'unanswered' : gwc[key] ? 'yes' : 'no'}`}
              title={label}
              onClick={() => {
                cycleGwc(key);
              }}
              className={`size-7 rounded-kw-md border text-[12px] font-medium transition-colors disabled:cursor-not-allowed ${
                gwc[key] === true
                  ? 'border-kw-tag-text/30 bg-kw-emerald-light text-kw-tag-text'
                  : gwc[key] === false
                    ? 'border-kw-danger-text/30 bg-kw-danger-bg text-kw-danger-text'
                    : 'border-kw-border bg-white text-kw-muted hover:bg-kw-bg'
              }`}
            >
              {gwc[key] === null ? short : gwc[key] ? 'Y' : 'N'}
            </button>
          </td>
        ))}
        <td className="px-2 py-2 text-center">
          {bar === 'incomplete' ? (
            <Badge variant="neutral">Incomplete</Badge>
          ) : bar === 'pass' ? (
            <Badge variant="success">Above the Bar</Badge>
          ) : (
            <Badge variant="danger">Below the Bar</Badge>
          )}
        </td>
        <td className="py-2 pl-2">
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              aria-label={`Notes for ${subjectName}`}
              onClick={() => {
                setNotesOpen((v) => !v);
              }}
              className={`rounded-kw-md border px-2 py-1 text-[11px] transition-colors ${
                notes.trim()
                  ? 'border-kw-cerulean/40 bg-kw-cerulean-light text-kw-cerulean-dark'
                  : 'border-kw-border bg-white text-kw-muted hover:bg-kw-bg'
              }`}
            >
              Notes
            </button>
            {!readOnly ? (
              <Button
                size="sm"
                variant="secondary"
                isLoading={saveMutation.isPending}
                disabled={!dirty}
                onClick={() => {
                  saveMutation.mutate();
                }}
              >
                Save
              </Button>
            ) : null}
          </div>
        </td>
      </tr>
      {notesOpen ? (
        <tr className="border-b border-kw-border/60">
          <td colSpan={coreValues.length + GWC_META.length + 3} className="py-2 pr-3">
            <textarea
              aria-label={`Notes text for ${subjectName}`}
              value={notes}
              maxLength={1000}
              rows={2}
              readOnly={readOnly}
              placeholder="Private leadership notes for this review…"
              onChange={(e) => {
                setNotes(e.target.value);
                setDirty(true);
              }}
              className="w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted read-only:bg-kw-bg focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Create round modal (manage_eos/superadmin)
// ─────────────────────────────────────────────────────────────────────────

function CreateRoundModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (round: PaRound) => void;
}) {
  const [label, setLabel] = useState('');
  const [quarterStart, setQuarterStart] = useState('');
  const [departmentId, setDepartmentId] = useState('');

  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: open,
  });

  const quarterOptions = useMemo(
    () => rollingQuarters(new Date(), 6).map((d) => ({ iso: toIsoDate(d), label: quarterLabel(d) })),
    [],
  );

  const mutation = useMutation({
    mutationFn: () =>
      createRound({
        label: label.trim(),
        quarterStart: quarterStart || undefined,
        departmentId: departmentId || undefined,
      }),
    onSuccess: (round) => {
      onCreated(round);
      onClose();
      setLabel('');
      setQuarterStart('');
      setDepartmentId('');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!label.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="New review round" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Label"
          name="round-label"
          required
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          placeholder="e.g. Q3 2026 leadership review"
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Quarter (optional)</span>
            <select
              value={quarterStart}
              onChange={(e) => {
                setQuarterStart(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">No quarter</option>
              {quarterOptions.map((q) => (
                <option key={q.iso} value={q.iso}>
                  {q.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Department (optional)</span>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">Org-wide</option>
              {(departmentsQuery.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeEosError(mutation.error, 'Could not create the round.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!label.trim()}>
            Create round
          </Button>
        </div>
      </form>
    </Modal>
  );
}
